import React, { useState } from 'react';
import { AuthenticationResponse } from 'data/schemas/auth';
import { RunbookSection, RunbookStep, RunbookTask } from 'data/schemas/MigrationRunbook';
import { SingleEmployeePicker } from 'presentations/view/TeamCalendar/components/EmployeePicker';
import { createStep, createTask, moveItem, resequenceSteps } from './runbookPlan.helpers';

interface Props {
    sections: RunbookSection[];
    employees: AuthenticationResponse[];
    disabled?: boolean;
    onChange: (sections: RunbookSection[]) => void;
}

const smallButton =
    'rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30';

export default function RunbookPlanBuilder({ sections, employees, disabled, onChange }: Props) {
    const [activeType, setActiveType] = useState<'execution' | 'rollback'>('execution');
    const [collapsedSteps, setCollapsedSteps] = useState<Record<string, boolean>>({});
    const sectionIndex = sections.findIndex(section => section.sectionType === activeType);
    const section = sections[sectionIndex];

    if (!section) return null;

    const replaceSteps = (steps: RunbookStep[]) => {
        onChange(
            sections.map((item, index) =>
                index === sectionIndex ? { ...item, steps: resequenceSteps(steps) } : item
            )
        );
    };

    const updateStep = (stepIndex: number, update: (step: RunbookStep) => RunbookStep) => {
        replaceSteps(
            section.steps.map((step, index) => (index === stepIndex ? update(step) : step))
        );
    };

    const updateTask = (
        stepIndex: number,
        taskIndex: number,
        update: (task: RunbookTask) => RunbookTask
    ) => {
        updateStep(stepIndex, step => ({
            ...step,
            tasks: step.tasks.map((task, index) => (index === taskIndex ? update(task) : task))
        }));
    };

    const employeeName = (citizenId?: string) =>
        employees.find(employee => employee.ctizenID === citizenId)?.nickName || citizenId || '';

    const stepKey = (step: RunbookStep, stepIndex: number) =>
        `${activeType}:${step.id || stepIndex}`;

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <header className="border-b border-gray-200 bg-gray-50 px-5 pt-4">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Migration checklist</h2>
                        <p className="text-sm text-gray-500">
                            Group related work into steps, then add the details in order.
                        </p>
                    </div>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                            replaceSteps([...section.steps, createStep(section.steps.length + 1)])
                        }
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        + Add step
                    </button>
                </div>
                <div className="mt-4 flex gap-5">
                    {(['execution', 'rollback'] as const).map(type => (
                        <button
                            key={type}
                            type="button"
                            onClick={() => setActiveType(type)}
                            className={`border-b-2 px-1 pb-3 text-sm font-semibold ${activeType === type ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500'}`}
                        >
                            {type === 'execution' ? 'Execution' : 'Rollback'}
                            <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                                {sections.find(item => item.sectionType === type)?.steps.length ??
                                    0}
                            </span>
                        </button>
                    ))}
                </div>
            </header>

            <div className="space-y-4 p-4 md:p-5">
                {section.steps.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
                        <p className="font-medium text-gray-700">
                            {activeType === 'rollback'
                                ? 'Rollback is optional. Add a recovery step when this migration needs one.'
                                : 'Add the first execution step to begin.'}
                        </p>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => replaceSteps([createStep(1)])}
                            className="mt-3 text-sm font-semibold text-blue-600"
                        >
                            + Add first step
                        </button>
                    </div>
                )}

                {section.steps.map((step, stepIndex) => (
                    <article
                        key={step.id || `${activeType}-${stepIndex}`}
                        className="rounded-xl border border-gray-200"
                    >
                        <div className="flex flex-wrap items-center gap-3 rounded-t-xl border-b border-gray-100 bg-slate-50 px-4 py-3">
                            <button
                                type="button"
                                title={
                                    collapsedSteps[stepKey(step, stepIndex)]
                                        ? 'Expand step'
                                        : 'Collapse step'
                                }
                                aria-label={
                                    collapsedSteps[stepKey(step, stepIndex)]
                                        ? 'Expand step'
                                        : 'Collapse step'
                                }
                                onClick={() => {
                                    const key = stepKey(step, stepIndex);
                                    setCollapsedSteps(current => ({
                                        ...current,
                                        [key]: !current[key]
                                    }));
                                }}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                            >
                                <svg
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    className={`h-4 w-4 transition-transform ${
                                        collapsedSteps[stepKey(step, stepIndex)] ? '-rotate-90' : ''
                                    }`}
                                >
                                    <path
                                        d="M5 7.5 10 12.5 15 7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                                {stepIndex + 1}
                            </span>
                            <input
                                disabled={disabled}
                                value={step.title}
                                onChange={event =>
                                    updateStep(stepIndex, current => ({
                                        ...current,
                                        title: event.target.value
                                    }))
                                }
                                placeholder={`Step ${stepIndex + 1} title`}
                                className="min-w-[220px] flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-blue-500"
                            />
                            <button
                                type="button"
                                disabled={disabled || stepIndex === 0}
                                onClick={() =>
                                    replaceSteps(moveItem(section.steps, stepIndex, stepIndex - 1))
                                }
                                className={smallButton}
                            >
                                Up
                            </button>
                            <button
                                type="button"
                                disabled={disabled || stepIndex === section.steps.length - 1}
                                onClick={() =>
                                    replaceSteps(moveItem(section.steps, stepIndex, stepIndex + 1))
                                }
                                className={smallButton}
                            >
                                Down
                            </button>
                            <button
                                type="button"
                                disabled={
                                    disabled ||
                                    (activeType === 'execution' && section.steps.length === 1)
                                }
                                onClick={() =>
                                    replaceSteps(
                                        section.steps.filter((_, index) => index !== stepIndex)
                                    )
                                }
                                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-30"
                            >
                                Remove
                            </button>
                        </div>

                        {!collapsedSteps[stepKey(step, stepIndex)] && (
                            <div className="space-y-3 p-4">
                                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                    <label className="w-full text-xs font-semibold text-gray-700 md:w-96">
                                        Step owner
                                        <div className="mt-1">
                                            <SingleEmployeePicker
                                                value={step.defaultAssigneeCitizenId || ''}
                                                employees={employees}
                                                disabled={disabled}
                                                placeholder="Select owner for all details"
                                                onChange={citizenId => {
                                                    const name = employeeName(citizenId);
                                                    updateStep(stepIndex, current => ({
                                                        ...current,
                                                        defaultAssigneeCitizenId: citizenId,
                                                        defaultAssigneeName: name,
                                                        tasks: current.tasks.map(task => ({
                                                            ...task,
                                                            assigneeCitizenId: citizenId,
                                                            assigneeName: name
                                                        }))
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </label>
                                    <p className="text-xs text-gray-500">
                                        Applied to all details. Override an individual detail only
                                        when needed.
                                    </p>
                                </div>
                                <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                    <summary className="cursor-pointer text-sm font-medium text-gray-700">
                                        Step note (optional)
                                    </summary>
                                    <div className="mt-3">
                                        <label className="text-xs font-medium text-gray-600">
                                            Step note
                                            <textarea
                                                disabled={disabled}
                                                value={step.description}
                                                onChange={event =>
                                                    updateStep(stepIndex, current => ({
                                                        ...current,
                                                        description: event.target.value
                                                    }))
                                                }
                                                rows={2}
                                                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                                            />
                                        </label>
                                    </div>
                                </details>

                                <div className="space-y-2">
                                    {step.tasks.map((task, taskIndex) => (
                                        <div
                                            key={task.id || `${step.id}-${taskIndex}`}
                                            className="rounded-lg border border-gray-200 bg-white px-3 py-3"
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">
                                                    {taskIndex + 1}
                                                </span>
                                                <input
                                                    disabled={disabled}
                                                    value={task.title}
                                                    onChange={event =>
                                                        updateTask(
                                                            stepIndex,
                                                            taskIndex,
                                                            current => ({
                                                                ...current,
                                                                title: event.target.value
                                                            })
                                                        )
                                                    }
                                                    placeholder="What needs to be done?"
                                                    className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                                                />
                                                {(task.assigneeName || task.assigneeCitizenId) && (
                                                    <span className="mt-1 hidden max-w-[150px] truncate rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-600 md:block">
                                                        {task.assigneeName ||
                                                            task.assigneeCitizenId}
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    disabled={disabled || taskIndex === 0}
                                                    onClick={() =>
                                                        updateStep(stepIndex, current => ({
                                                            ...current,
                                                            tasks: moveItem(
                                                                current.tasks,
                                                                taskIndex,
                                                                taskIndex - 1
                                                            )
                                                        }))
                                                    }
                                                    className={smallButton}
                                                >
                                                    Up
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={
                                                        disabled ||
                                                        taskIndex === step.tasks.length - 1
                                                    }
                                                    onClick={() =>
                                                        updateStep(stepIndex, current => ({
                                                            ...current,
                                                            tasks: moveItem(
                                                                current.tasks,
                                                                taskIndex,
                                                                taskIndex + 1
                                                            )
                                                        }))
                                                    }
                                                    className={smallButton}
                                                >
                                                    Down
                                                </button>
                                            </div>
                                            <details className="ml-9 mt-2">
                                                <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-blue-700">
                                                    Options
                                                </summary>
                                                <div className="mt-2 grid gap-3 rounded-lg bg-gray-50 p-3 md:grid-cols-2">
                                                    <label className="text-xs font-medium text-gray-600 md:col-span-2">
                                                        Command or note
                                                        <textarea
                                                            disabled={disabled}
                                                            value={task.detail}
                                                            onChange={event =>
                                                                updateTask(
                                                                    stepIndex,
                                                                    taskIndex,
                                                                    current => ({
                                                                        ...current,
                                                                        detail: event.target.value
                                                                    })
                                                                )
                                                            }
                                                            rows={2}
                                                            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                                                        />
                                                    </label>
                                                    <label className="text-xs font-medium text-gray-600">
                                                        Assignee override
                                                        <div className="mt-1">
                                                            <SingleEmployeePicker
                                                                value={task.assigneeCitizenId}
                                                                employees={employees}
                                                                disabled={disabled}
                                                                placeholder="Use step default"
                                                                onChange={citizenId =>
                                                                    updateTask(
                                                                        stepIndex,
                                                                        taskIndex,
                                                                        current => ({
                                                                            ...current,
                                                                            assigneeCitizenId:
                                                                                citizenId,
                                                                            assigneeName:
                                                                                employeeName(
                                                                                    citizenId
                                                                                )
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </label>
                                                    <label className="text-xs font-medium text-gray-600">
                                                        Planned minutes
                                                        <input
                                                            disabled={disabled}
                                                            type="number"
                                                            min={0}
                                                            value={task.plannedMinutes}
                                                            onChange={event =>
                                                                updateTask(
                                                                    stepIndex,
                                                                    taskIndex,
                                                                    current => ({
                                                                        ...current,
                                                                        plannedMinutes:
                                                                            Number(
                                                                                event.target.value
                                                                            ) || 0
                                                                    })
                                                                )
                                                            }
                                                            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                                                        />
                                                    </label>
                                                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                                                        <input
                                                            disabled={disabled}
                                                            type="checkbox"
                                                            checked={task.isRequired}
                                                            onChange={event =>
                                                                updateTask(
                                                                    stepIndex,
                                                                    taskIndex,
                                                                    current => ({
                                                                        ...current,
                                                                        isRequired:
                                                                            event.target.checked
                                                                    })
                                                                )
                                                            }
                                                        />
                                                        Required before completing the run
                                                    </label>
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            disabled || step.tasks.length === 1
                                                        }
                                                        onClick={() =>
                                                            updateStep(stepIndex, current => ({
                                                                ...current,
                                                                tasks: current.tasks.filter(
                                                                    (_, index) =>
                                                                        index !== taskIndex
                                                                )
                                                            }))
                                                        }
                                                        className="justify-self-start text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-30"
                                                    >
                                                        Delete detail
                                                    </button>
                                                </div>
                                            </details>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() =>
                                        updateStep(stepIndex, current => {
                                            const task = createTask(current.tasks.length + 1);
                                            return {
                                                ...current,
                                                tasks: [
                                                    ...current.tasks,
                                                    {
                                                        ...task,
                                                        assigneeCitizenId:
                                                            current.defaultAssigneeCitizenId || '',
                                                        assigneeName:
                                                            current.defaultAssigneeName || ''
                                                    }
                                                ]
                                            };
                                        })
                                    }
                                    className="text-sm font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                >
                                    + Add detail
                                </button>
                            </div>
                        )}
                    </article>
                ))}
            </div>
        </section>
    );
}
